import { courseChatService, type CourseChatScope } from "services/courseChatService";

/**
 * In-memory cache for temporary R2 signed attachment URLs.
 *
 * Signed URLs are short-lived and MUST NOT be persisted (no localStorage). They
 * are resolved through Laravel and cached only for the lifetime of the tab so we
 * don't re-request the same URL on every Firestore snapshot/re-render.
 */
type CacheEntry = { url: string; expiresAt: number };

const cache = new Map<string, CacheEntry>();
const inflight = new Map<string, Promise<string>>();

// Conservatively treat cached URLs as valid for a few minutes even though the
// backend TTL is longer; on any 403/expired playback we force-refresh.
const SOFT_TTL_MS = 5 * 60 * 1000;

function key(scope: CourseChatScope, courseId: number | string, messageId: string): string {
  return `${scope}:${courseId}:${messageId}`;
}

export async function getAttachmentUrl(
  scope: CourseChatScope,
  courseId: number | string,
  messageId: string,
  forceRefresh = false
): Promise<string> {
  const k = key(scope, courseId, messageId);

  if (!forceRefresh) {
    const cached = cache.get(k);
    if (cached && cached.expiresAt > Date.now()) {
      return cached.url;
    }
    const pending = inflight.get(k);
    if (pending) return pending;
  } else {
    cache.delete(k);
  }

  const promise = courseChatService
    .resolveAttachmentUrl(scope, courseId, messageId)
    .then((url) => {
      cache.set(k, { url, expiresAt: Date.now() + SOFT_TTL_MS });
      inflight.delete(k);
      return url;
    })
    .catch((err) => {
      inflight.delete(k);
      throw err;
    });

  inflight.set(k, promise);
  return promise;
}

export function invalidateAttachmentUrl(
  scope: CourseChatScope,
  courseId: number | string,
  messageId: string
): void {
  cache.delete(key(scope, courseId, messageId));
}
